/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import { useEffect, useMemo, useState } from 'react';
import { parse } from 'yaml';
import { BASE_BUCKET_URL, CDN_APP_URL } from '@/config';

export type SupportedOS = 'windows' | 'mac' | 'linux' | 'android' | 'unknown';
const ANDROID_APK_URL = 'https://ccalls.site/releases/app-release.apk';

interface DownloadState {
  os: SupportedOS;
  links: {
    windows?: string;
    mac?: string;
    linux?: string;
    android?: string;
  };
  versions: {
    windows?: string;
    mac?: string;
    linux?: string;
    android?: string;
  };
  isReady: boolean;
}

function detectOS(): SupportedOS {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('android')) return 'android';
  if (ua.includes('win')) return 'windows';
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('linux')) return 'linux';

  return 'unknown';
}

/**
 * @example
 *   const { downloadUrl, isReady } = useAppDownload();
 *
 *   if ( !isReady || !downloadUrl) {
 *     return null;
 *   }
 *
 *   return (
 *       <button
 *         onClick={() => window.open(downloadUrl, '_blank')}
 *       >
 *       </button>
 *   );
 */
export function useAppDownload() {
  const [state, setState] = useState<DownloadState>({
    os: 'unknown',
    links: {},
    versions: {},
    isReady: false,
  });

  useEffect(() => {
    const os = detectOS();

    const load = async () => {
      try {
        const [win, mac, linux] = await Promise.all([
          fetch(`${CDN_APP_URL}latest.yml`).then((r) => r.text()),
          fetch(`${CDN_APP_URL}latest-mac.yml`).then((r) => r.text()),
          fetch(`${CDN_APP_URL}latest-linux.yml`).then((r) => r.text()),
        ]);

        const winData = parse(win);
        const macData = parse(mac);
        const linuxData = parse(linux);

        setState({
          os,
          links: {
            windows: `${CDN_APP_URL}${winData.files[0].url}`,
            mac: `${CDN_APP_URL}${macData.files[0].url}`,
            linux: `${CDN_APP_URL}${linuxData.files[0].url}`,
            android: ANDROID_APK_URL,
          },
          versions: {
            windows: winData.version,
            mac: macData.version,
            linux: linuxData.version,
            android: undefined,
          },
          isReady: true,
        });

        console.log('CDN got');
      } catch (error) {
        console.error('Failed to load download links from CDN:', error);

        setState({
          os,
          links: {
            windows: `${CDN_APP_URL}CyberKoala Calls Setup 2.8.3.exe`,
            mac: `${CDN_APP_URL}CyberKoala Calls-2.3.7-arm64.dmg`,
            linux: `${CDN_APP_URL}CyberKoala Calls-2.8.3.AppImage`,
            android: ANDROID_APK_URL,
          },
          versions: {},
          isReady: true,
        });
      }
    };

    load();
  }, []);

  const downloadUrl = useMemo(() => {
    if (state.os === 'unknown') return null;
    return state.links[state.os] ?? null;
  }, [state]);

  const version = useMemo(() => {
    if (state.os === 'unknown') {
      return null;
    }

    return state.versions[state.os] ?? null;
  }, [state.os, state.versions]);

  return {
    os: state.os,
    version,
    downloadUrl,
    links: state.links,
    versions: state.versions,
    isReady: state.isReady,
  };
}


/**
 * Hook for downloading desktop application from local CDN folder
 * TODO: this is causing CORS errors blocked by CORS policy: No 'Access-Control-Allow-Origin' header is present on the requested resource.
 * @example
 *   const { downloadUrl, isReady, os, links, versions } = useAppDownload();
 *
 *   if (!isReady) {
 *     return <Spinner />;
 *   }
 *
 *   return (
 *     <div>
 *       {downloadUrl && (
 *         <button onClick={() => window.open(downloadUrl, '_blank')}>
 *           Download for {os === 'windows' ? 'Windows' : os === 'mac' ? 'macOS' : 'Linux'}
 *         </button>
 *       )}
 *     </div>
 *   );
 */
export function useAppDownloadStatic() {
  const [state, setState] = useState<DownloadState>({
    os: 'unknown',
    links: {},
    versions: {},
    isReady: false,
  });

  useEffect(() => {
    const os = detectOS();

    const load = async () => {
      try {
        const [win, mac, linux] = await Promise.all([
          fetch(`${CDN_APP_URL}latest.yml`).then((r) => r.text()),
          fetch(`${CDN_APP_URL}latest-mac.yml`).then((r) => r.text()),
          fetch(`${CDN_APP_URL}latest-linux.yml`).then((r) => r.text()),
        ]);

        const winData = parse(win);
        const macData = parse(mac);
        const linuxData = parse(linux);

        setState({
          os,
          links: {
            windows: `${CDN_APP_URL}${winData.files[0].url}`,
            mac: `${CDN_APP_URL}${macData.files[0].url}`,
            linux: `${CDN_APP_URL}${linuxData.files[0].url}`,
          },
          versions: {
            windows: winData.version,
            mac: macData.version,
            linux: linuxData.version,
          },
          isReady: true,
        });

        console.log("CDN got")
      } catch (error) {
        console.error('Failed to load download links from CDN:', error);

        setState({
          os,
          links: {
            windows: `${CDN_APP_URL}CyberKoala Calls Setup 2.3.19.exe`,
            mac: `${CDN_APP_URL}CyberKoala Calls-2.3.7-arm64.dmg`,
            linux: `${CDN_APP_URL}CyberKoala Calls-2.3.17.AppImage`,
          },
          versions: {},
          isReady: true,
        });
      }
    };

    load();
  }, []);

  const downloadUrl = useMemo(() => {
    if (state.os === 'unknown') return null;
    return state.links[state.os] ?? null;
  }, [state]);

  return {
    os: state.os,
    downloadUrl,
    links: state.links,
    versions: state.versions,
    isReady: state.isReady,
  };
}

